import { Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { useSesion } from '../lib/sesion.ts';
import {
  guardarSeleccion,
  useFacetas,
  useProductosCatalogo,
  type FiltrosCatalogo,
} from '../lib/catalogo.ts';
import { Search } from 'lucide-react';
import { Boton, Cargando, Chip, EncabezadoPagina } from '../componentes/ui.tsx';
import { PasosPost } from '../componentes/PasosPost.tsx';
import { BannerCampania } from '../componentes/BannerCampania.tsx';

interface MarcaResumen {
  id: string;
  nombre: string;
  logoUrl: string | null;
  colorPrimario: string | null;
}

/**
 * Panel de productos (SPEC §6.2): derivado de compras, filtros, selección con barra fija.
 * Scroll infinito por cursor; los cientos de filas reales no se montan de golpe.
 */
export function PaginaProductos() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const busqueda = useSearch({ strict: false }) as { tipoContenido?: string; marca?: string };
  const tipoContenido = busqueda.tipoContenido ?? 'PROMOCION';
  const { data: sesion } = useSesion();

  // Con ?marca= en la URL (accesos de marca del inicio) se salta el selector.
  const [filtros, setFiltros] = useState<FiltrosCatalogo>(busqueda.marca ? { marca: busqueda.marca } : {});
  const [q, setQ] = useState('');
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());

  const marcas = useQuery({
    queryKey: ['catalogo-marcas'],
    queryFn: () => api<{ items: MarcaResumen[] }>('/catalogo/marcas'),
    staleTime: 5 * 60 * 1000,
  });
  const facetas = useFacetas();
  // Un arte lleva productos de UNA sola marca: sin marca elegida no se carga nada.
  const productos = useProductosCatalogo({ ...filtros, q: q || undefined }, Boolean(filtros.marca));
  const marcaElegida = marcas.data?.items.find((m) => m.id === filtros.marca) ?? null;

  // Con una sola marca comprada no hay nada que elegir.
  useEffect(() => {
    const items = marcas.data?.items;
    if (!filtros.marca && items?.length === 1) {
      setFiltros((f) => ({ ...f, marca: items[0]!.id }));
    }
  }, [marcas.data, filtros.marca]);

  // La marca se elige en el paso 0 (/marca): sin marca aquí, de vuelta a ese paso.
  useEffect(() => {
    const items = marcas.data?.items;
    if (!filtros.marca && items && items.length > 1) {
      void navegar({ to: '/marca', search: { tipoContenido } as never, replace: true });
    }
  }, [filtros.marca, marcas.data, navegar, tipoContenido]);

  function cambiarMarca() {
    setSeleccion(new Set());
    void navegar({ to: '/marca', search: { tipoContenido } as never });
  }

  const filas = useMemo(
    () => productos.data?.pages.flatMap((p) => p.items) ?? [],
    [productos.data],
  );

  // Scroll infinito con IntersectionObserver: sin :hover ni mousemove (regla 10).
  const centinela = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nodo = centinela.current;
    if (!nodo) return;
    const observador = new IntersectionObserver((entradas) => {
      if (entradas[0]?.isIntersecting && productos.hasNextPage && !productos.isFetchingNextPage) {
        void productos.fetchNextPage();
      }
    });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [productos]);

  function alternar(id: string) {
    setSeleccion((previa) => {
      const nueva = new Set(previa);
      if (nueva.has(id)) nueva.delete(id);
      else nueva.add(id);
      return nueva;
    });
  }

  function seleccionarTodos() {
    // Aplica al filtro activo (lo ya cargado); avisa cuántos son (SPEC §6.2).
    setSeleccion(new Set(filas.map((f) => f.id)));
  }

  function continuar() {
    // Paso 1 → paso 2: con los productos elegidos se va a escoger el diseño.
    guardarSeleccion({ productoIds: [...seleccion], tipoContenido });
    void navegar({
      to: '/plantillas',
      search: { tipoContenido, ...(filtros.marca ? { marca: filtros.marca } : {}) } as never,
    });
  }

  const puedeVerCosto = sesion?.puedeVerCosto ?? false;

  if (!filtros.marca) return <Cargando texto={t('comun.cargando')} />;

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4 pb-28">
      <BannerCampania seccion="productos_h" />
      {/* El flujo manda: paso 1 grande arriba */}
      <div className="flex justify-center py-2">
        <PasosPost paso={1} />
      </div>
      <EncabezadoPagina
        titulo={t('productos.titulo')}
        subtitulo={`${filas.length} ${t('inicio.statProductos')}`}
        acciones={
          <div className="flex items-center gap-3">
            <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-suave" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`${t('productos.buscar')}…`}
              className="campo con-icono w-64 bg-superficie shadow-[var(--sombra)]"
            />
            </div>
          </div>
        }
      />

      {/* Filas de chips de filtro (referencia): marcas, colecciones y categorías */}
      <div className="flex flex-wrap items-center gap-2">
        <Chip activo onClick={cambiarMarca}>
          {marcaElegida?.nombre ?? ''}
        </Chip>
        <Boton variante="texto" tamano="sm" onClick={cambiarMarca}>
          {t('productos.cambiarMarca')}
        </Boton>
        <span className="mx-1 h-5 w-px bg-superficie2" />
        {facetas.data?.colecciones.map((c) => (
          <Chip
            key={c}
            activo={filtros.coleccion === c}
            onClick={() =>
              setFiltros((f) => ({ ...f, coleccion: f.coleccion === c ? undefined : c }))
            }
          >
            {c}
          </Chip>
        ))}
        {facetas.data?.categorias.map((c) => (
          <Chip
            key={c}
            activo={filtros.categoria === c}
            onClick={() =>
              setFiltros((f) => ({ ...f, categoria: f.categoria === c ? undefined : c }))
            }
          >
            {c}
          </Chip>
        ))}
      </div>

      {productos.isPending ? <Cargando texto={t('comun.cargando')} /> : null}

      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        {filas.map((p) => {
          const marcado = seleccion.has(p.id);
          return (
            <li key={p.id}>
              {/* Toda la tarjeta es tocable, no sólo el checkbox (D31). */}
              <button
                type="button"
                onClick={() => alternar(p.id)}
                aria-pressed={marcado}
                className="tarjeta w-full p-3 text-left transition hover:-translate-y-0.5"
                style={
                  marcado
                    ? { backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }
                    : undefined
                }
              >
                <div className="relative aspect-square overflow-hidden rounded-2xl bg-superficie2">
                  <img
                    src={p.imagenUrl}
                    alt={p.nombre}
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                  {marcado ? (
                    <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-negro text-white">
                      <Check size={14} />
                    </span>
                  ) : null}
                </div>
                <p className="mt-2.5 truncate text-[15px] font-medium">{p.nombre}</p>
                <p className={`text-xs ${marcado ? 'opacity-70' : 'text-suave'}`}>
                  {p.marcaNombre}
                  {puedeVerCosto && p.costo !== undefined ? ` · ${t('productos.costo')} $${p.costo.toFixed(2)}` : ''}
                </p>
                <p className={`text-xs ${marcado ? 'opacity-70' : 'text-suave'}`}>
                  {t('productos.facturado', { fecha: new Date(p.fechaCompra).toLocaleDateString() })}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span
                    className="tag font-mono"
                    style={marcado ? { backgroundColor: 'rgba(0,0,0,0.12)', color: 'inherit' } : undefined}
                  >
                    {p.referencia}
                  </span>
                  {p.precioGuardado ? (
                    <span
                      className="tag"
                      style={
                        marcado
                          ? { backgroundColor: '#101113', color: '#fff' }
                          : { backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }
                      }
                    >
                      ${p.precioGuardado.precioAhora.toFixed(2)}
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {filas.length === 0 && !productos.isPending ? (
        <p className="py-10 text-center text-sm text-suave">{t('productos.vacio')}</p>
      ) : null}
      <div ref={centinela} className="h-4" />
      {productos.isFetchingNextPage ? <Cargando texto={t('comun.cargando')} /> : null}

      {/* Barra fija: Seleccionar todos / Limpiar / Continuar, con contador (SPEC §6.2). */}
      <div className="sticky bottom-4 z-30 mt-6">
        <div className="tarjeta flex items-center justify-between gap-2 px-4 py-3 shadow-[var(--sombra-alta)]">
          <div className="flex items-center gap-2">
            <Boton variante="secundario" onClick={seleccionarTodos}>
              {t('productos.seleccionarTodos', { n: filas.length })}
            </Boton>
            <Boton variante="texto" onClick={() => setSeleccion(new Set())}>
              {t('productos.limpiar')}
            </Boton>
          </div>
          <div className="flex items-center gap-3">
            <Boton disabled={seleccion.size === 0} onClick={continuar}>
              {t('productos.continuar', { n: seleccion.size })}
            </Boton>
          </div>
        </div>
      </div>
    </main>
  );
}
